import React, { useEffect } from 'react';
import {
  MessageCircle,
  ArrowRight,
  Check,
  AlertTriangle,
  Star,
  Shield,
  Clock,
  ThumbsUp,
  TrendingUp,
  X
} from 'lucide-react';

const RespondNegativeReviews = () => {
  useEffect(() => {
    // Google Analytics
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-V8ESDMZ5NZ');

    // SEO-OPTIMIZED: Title and meta description
    document.title = 'How to Respond to Negative Reviews Fast (Before They Hurt Sales) | TicketDesk';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Negative reviews on Trustpilot, Google, or Capterra can kill sales if ignored. Learn proven strategies to respond quickly, turn unhappy customers around, and protect your reputation.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://www.ticketdesk.app/blog/respond-negative-reviews-fast';

    // Structured data for article
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "How to Respond to Negative Reviews Fast (Before They Hurt Sales)",
      "description": "Practical guide to responding to negative online reviews on Trustpilot, Google, Capterra, and G2 with response templates and timing strategies",
      "author": {
        "@type": "Organization",
        "name": "TicketDesk"
      },
      "publisher": {
        "@type": "Organization",
        "name": "TicketDesk",
        "logo": {
          "@type": "ImageObject",
          "url": "https://ticketdesk.app/logo.png"
        }
      },
      "datePublished": "2025-01-10",
      "dateModified": "2025-01-10"
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }, []);

  const trackEvent = (eventName, eventParams = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, eventParams);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle">
                  <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                </svg>
              </div>
              <span className="ml-2 sm:ml-3 text-lg sm:text-xl font-semibold text-gray-900">TicketDesk</span>
            </a>
            <div className="flex items-center space-x-2 sm:space-x-4 lg:space-x-8">
              <a href="/blog" className="text-xs sm:text-sm text-purple-600 font-medium hidden md:block">Blog</a>
              <a href="/#features" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden md:block">Features</a>
              <a href="/#pricing" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900">Pricing</a>
              <a href="/login" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden lg:block">Sign In</a>
              <a href="/register" className="bg-purple-600 text-white px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-purple-700 whitespace-nowrap">
                Try free
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <a href="/blog" className="text-purple-600 hover:text-purple-700 font-medium text-sm inline-flex items-center mb-4">
            ← Back to Blog
          </a>
          <div className="mb-4">
            <span className="inline-block bg-red-100 text-red-700 text-xs font-semibold px-3 py-1 rounded-full">
              Reputation Management
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            How to Respond to Negative Reviews Fast (Before They Hurt Sales)
          </h1>
          <div className="flex items-center text-gray-600 text-sm">
            <Clock className="w-4 h-4 mr-1" />
            <span className="mr-4">8 min read</span>
            <span>January 10, 2025</span>
          </div>
        </div>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            A 1-star review appears on Trustpilot at 9:47 AM Tuesday. By Friday afternoon, 23 potential customers have read it—and 19 of them clicked away without buying. Each negative review left unaddressed doesn't just represent one unhappy customer. It's a billboard on the internet telling prospects your business doesn't care about customer problems.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Research consistently shows that responding to negative reviews—especially responding quickly—significantly impacts both conversion rates and overall reputation. BrightLocal's 2024 survey found that 89% of consumers read business responses to reviews, and businesses that respond to negative feedback see 33% higher trust ratings than those that ignore criticism. Speed matters: responses within 24 hours demonstrate attentiveness that turns fence-sitters into customers.
          </p>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 my-8 rounded-r-lg">
            <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
              The Real Cost of Ignoring Negative Reviews
            </h3>
            <ul className="space-y-2 text-gray-700 list-disc list-inside">
              <li>94% of consumers avoid businesses with negative reviews (ReviewTrackers)</li>
              <li>One negative review can cost a business approximately 30 customers (Harvard Business School)</li>
              <li>53% of customers expect businesses to respond to negative reviews within a week</li>
              <li>45% of consumers are more likely to visit businesses that respond to negative reviews</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why Speed Matters More Than Perfection</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            The difference between responding in 2 hours versus 2 days fundamentally changes how both the reviewer and future readers perceive your business. A fast response signals:
          </p>

          <ul className="space-y-3 text-gray-700 mb-6 ml-6">
            <li className="flex items-start">
              <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>You're paying attention.</strong> Businesses that respond quickly clearly monitor customer feedback and take it seriously.</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>You care about resolution.</strong> Fast responses suggest genuine interest in fixing problems, not defensive corporate PR.</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>This was an isolated incident.</strong> Quick action implies the problem isn't widespread or ignored company-wide.</span>
            </li>
            <li className="flex items-start">
              <Check className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span><strong>You might deserve a second chance.</strong> Prospects reading reviews see responsive businesses as lower-risk purchases.</span>
            </li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-6">
            Conversely, reviews that sit unanswered for days or weeks send a clear message: "We don't actually care what customers think." Potential buyers internalize this. They imagine themselves encountering a problem and receiving the same silence.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Negative Review Response Framework</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Responding to criticism is uncomfortable. But a simple framework makes it manageable and effective. Every good response includes four elements:
          </p>

          <div className="space-y-6 my-8">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-2">1. Acknowledge & Apologize</h3>
              <p className="text-gray-700 mb-3">Start by validating their experience and expressing genuine regret. Even if they're wrong or unreasonable, their frustration is real.</p>
              <p className="text-sm text-gray-600 italic">"I'm really sorry to hear about your experience with the damaged product. That's absolutely not the standard we aim for, and I understand your frustration."</p>
            </div>

            <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-2">2. Take Specific Responsibility</h3>
              <p className="text-gray-700 mb-3">Address their specific complaint. Generic responses feel like copy-paste PR. Show you actually read what they wrote.</p>
              <p className="text-sm text-gray-600 italic">"You mentioned the package arrived with a torn box and the product inside was cracked. This suggests a shipping handling issue we need to investigate with our carrier."</p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-2">3. Offer a Solution</h3>
              <p className="text-gray-700 mb-3">Explain what you're going to do about it. Be specific and actionable. Vague promises ("we'll do better") mean nothing.</p>
              <p className="text-sm text-gray-600 italic">"I'd like to send you a replacement immediately with upgraded shipping at no cost. If you could email me at support@example.com with your order number, I'll process this today."</p>
            </div>

            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-2">4. Move to Private Communication</h3>
              <p className="text-gray-700 mb-3">Provide a direct contact method to resolve details privately. Public responses should be professional; detailed resolution happens via email or phone.</p>
              <p className="text-sm text-gray-600 italic">"Please reach out directly so we can make this right. I'm personally handling this to ensure it gets resolved quickly."</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Response Templates for Common Scenarios</h2>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Template: Product Defect or Damage</h3>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
            <p className="text-gray-700 mb-4">
              "Thank you for taking the time to leave this feedback, and I'm truly sorry about the [specific issue]. This isn't representative of our quality standards, and I completely understand your disappointment.
            </p>
            <p className="text-gray-700 mb-4">
              We'd like to send you a replacement [product] right away along with a prepaid return label for the defective item. I'm also including [additional compensation, e.g., 20% discount on your next order] for the inconvenience this caused.
            </p>
            <p className="text-gray-700">
              Please email me directly at [email] with your order number, and I'll handle this personally to make sure it's resolved within 24 hours. Thank you for giving us the opportunity to make this right."
            </p>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Template: Shipping or Delivery Issues</h3>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
            <p className="text-gray-700 mb-4">
              "I'm really sorry your order arrived late. We know how frustrating it is to wait longer than expected, especially when you're counting on receiving something by a certain date.
            </p>
            <p className="text-gray-700 mb-4">
              The delay was due to [specific reason if known, e.g., carrier delays during the holiday rush]. While we can't control carrier timelines once packages ship, we should have communicated the delay to you proactively—and we didn't. That's on us.
            </p>
            <p className="text-gray-700">
              I'd like to refund your shipping cost and provide a [discount/credit] toward a future order. Please contact me at [email] so I can process this immediately."
            </p>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Template: Poor Customer Service Experience</h3>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6">
            <p className="text-gray-700 mb-4">
              "I'm genuinely sorry that your experience with our support team didn't meet expectations. You reached out for help and didn't receive the assistance you deserved—that's unacceptable.
            </p>
            <p className="text-gray-700 mb-4">
              I've reviewed your support conversation with [team member if applicable] and identified where we dropped the ball. We're addressing this internally to ensure better responses in the future, and I appreciate you bringing it to our attention.
            </p>
            <p className="text-gray-700">
              I'd like to personally help resolve your original issue and make up for the poor experience. Please reach out to me directly at [email] or call [phone], and I'll prioritize getting this fixed for you."
            </p>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-8 my-12 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Never Miss Another Negative Review</h3>
            <p className="text-gray-700 mb-6">
              TicketDesk monitors Trustpilot, Capterra, G2, and Google Reviews. Get instant alerts when reviews under 3 stars appear so you can respond within hours, not days.
            </p>
            <a
              href="/register"
              onClick={() => trackEvent('cta_click', { location: 'negative_reviews_midcontent' })}
              className="inline-flex items-center justify-center bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-purple-700"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <p className="text-sm text-gray-600 mt-4">No credit card required • Free plan available</p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What NOT to Do When Responding</h2>

          <div className="space-y-4 my-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                <X className="w-5 h-5 mr-2 text-red-500" />
                Don't Get Defensive or Argue
              </h4>
              <p className="text-sm text-gray-700">Even if the customer is factually wrong, arguing publicly makes you look petty and unprofessional. Future customers reading the exchange will side with the reviewer, not you.</p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                <X className="w-5 h-5 mr-2 text-red-500" />
                Don't Blame the Customer
              </h4>
              <p className="text-sm text-gray-700">"You should have read the instructions" or "This was clearly stated on our website" shifts blame and escalates negativity. Take responsibility first, clarify details privately later.</p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                <X className="w-5 h-5 mr-2 text-red-500" />
                Don't Use Generic Copy-Paste Responses
              </h4>
              <p className="text-sm text-gray-700">Reviewers and readers immediately recognize templated responses that don't address specific issues. This signals you're not actually reading feedback—just going through PR motions.</p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                <X className="w-5 h-5 mr-2 text-red-500" />
                Don't Make Promises You Can't Keep
              </h4>
              <p className="text-sm text-gray-700">Only offer solutions you can actually deliver. "We'll refund you immediately" when refunds take 5-7 business days sets false expectations and generates follow-up complaints.</p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center">
                <X className="w-5 h-5 mr-2 text-red-500" />
                Don't Ignore Obviously Fake Reviews
              </h4>
              <p className="text-sm text-gray-700">If a review is clearly from a competitor or fake (mentions products you don't sell, etc.), respond professionally noting the inaccuracy and report it to the platform. Silent ignoring makes it look legitimate.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Building a Review Monitoring System</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Fast response requires fast notification. You can't respond in 2 hours if you discover reviews 2 days later. Build a system:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Option 1: Manual Daily Checks</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Set a daily calendar reminder to check Trustpilot, Google Reviews, Capterra, G2, and any other platforms where you have listings. Takes 10-15 minutes daily. Works for businesses with low review volume (under 5 reviews per week).
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Option 2: Platform Email Alerts</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Most review platforms offer email notifications for new reviews. Enable these for all your accounts. Limitation: emails can get buried in your inbox, and you're still checking multiple platforms manually to respond.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Option 3: Automated Review Monitoring (Recommended)</h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            Software like TicketDesk monitors multiple review platforms automatically and sends instant alerts when new reviews appear—particularly focusing on reviews under 3 stars that require immediate attention. Each review becomes a trackable ticket in your support system with the customer context and review content, allowing you to draft responses, assign to team members, and track resolution just like customer support inquiries.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            This is the only scalable option for businesses receiving 5+ reviews per week or managing multiple locations/products across different platforms.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Turning Reviews Into Reputation Assets</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Here's the counterintuitive truth about negative reviews: they can actually increase trust and conversions when handled well. A business with only 5-star reviews looks suspicious—potential customers assume you're filtering feedback. A mix of 4-5 star reviews with some criticism (that you've responded to professionally) looks authentic.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Studies show that products with an average rating of 4.2-4.5 stars actually convert better than those with perfect 5.0 ratings. The key is demonstrating accountability through your responses. When prospects read negative reviews followed by thoughtful, solution-focused responses, they think: "Okay, things sometimes go wrong, but this company actually fixes problems."
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Your response to criticism becomes proof of your customer service quality. Use it strategically.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Final Thoughts</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Negative reviews are inevitable. Perfect businesses don't exist—and if they did, customers wouldn't trust the reviews anyway. What separates successful businesses from struggling ones isn't avoiding criticism; it's responding to criticism quickly, professionally, and with genuine intent to fix problems.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Speed matters. Within 24 hours is good. Within 2-3 hours is exceptional. Build a system that alerts you immediately when negative reviews appear so you can respond before they cost you sales.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Every negative review is an opportunity to demonstrate your customer service values to hundreds of future prospects. Use them wisely.
          </p>

          <div className="bg-purple-600 text-white rounded-xl p-8 my-12 text-center">
            <h3 className="text-2xl font-bold mb-4">Get Instant Negative Review Alerts</h3>
            <p className="text-lg mb-6 opacity-90">
              Monitor Trustpilot, Capterra, G2, and Google Reviews automatically. Respond within hours, not days.
            </p>
            <a
              href="/register"
              onClick={() => trackEvent('cta_click', { location: 'negative_reviews_bottom' })}
              className="inline-flex items-center justify-center bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <p className="text-sm mt-4 opacity-75">No credit card required • Free plan available</p>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 mt-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold">TicketDesk</span>
              </div>
              <p className="text-gray-400 max-w-md">
                Customer support software for small businesses. Unify Gmail, Facebook, WhatsApp, and review monitoring in one simple inbox.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/#features" className="hover:text-white">Features</a></li>
                <li><a href="/#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/for-ecommerce" className="hover:text-white">For E-commerce</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Resources</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/blog" className="hover:text-white">Blog</a></li>
                <li><a href="/compare/zendesk" className="hover:text-white">vs Zendesk</a></li>
                <li><a href="/register" className="hover:text-white">Start Free Trial</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 TicketDesk. Customer support software for small businesses.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RespondNegativeReviews;
